/// <amd-module name="VSSUI/Components/Panel/CustomPanel" />
import * as React from "react";
import { ScreenContext, ScreenSize } from "VSS/Core/Util/Screen";
import { Callout, ContentJustification, ContentLocation, ContentOrientation, ContentSize } from "VSSUI/Callout";
import { ICustomPanelProps, IPanel } from "VSSUI/Components/Panel/Panel.Props";
import { FocusZoneKeyStroke } from "VSSUI/FocusZone";
import { Observer } from "VSSUI/Observer";
import { Spacing, Surface, SurfaceContext } from "VSSUI/Surface";
import { css, KeyCode } from "VSSUI/Util";

interface ICustomPanelState {
    isDisplayed: boolean;
}

export class CustomPanel extends React.Component<ICustomPanelProps, ICustomPanelState> implements IPanel {
    public static defaultProps = {
        escDismiss: true,
        lightDismiss: true,
        modal: true
    };

    public static contextType = ScreenContext;

    private calloutContentRef = React.createRef<HTMLDivElement>();

    constructor(props: ICustomPanelProps) {
        super(props);

        // TODO: Change to false in M150
        this.state = {
            isDisplayed: true
        };
    }

    public render(): JSX.Element {
        const {
            ariaLabel,
            ariaLabelledBy,
            blurDismiss,
            calloutClassName,
            children,
            className,
            contentClassName,
            escDismiss,
            id,
            lightDismiss,
            modal,
            onDismiss,
            portalProps,
            size = ContentSize.Medium
        } = this.props;
        const { isDisplayed } = this.state;

        return (
            <Observer size={this.context.size}>
                {(props: { size: ScreenSize }) => {
                    const fullscreen = props.size === ScreenSize.xsmall;

                    return (
                        <Callout
                            ariaLabel={ariaLabel}
                            ariaLabelledBy={ariaLabelledBy}
                            blurDismiss={blurDismiss}
                            className={css("bolt-panel", calloutClassName, isDisplayed === false && "no-events")}
                            contentClassName={css(
                                contentClassName,
                                "bolt-panel-callout-content scroll-auto",
                                isDisplayed ? "in" : "out",
                                fullscreen ? "bolt-panel-fullscreen absolute-fill" : "relative"
                            )}
                            contentJustification={ContentJustification.Stretch}
                            contentLocation={ContentLocation.End}
                            contentOrientation={ContentOrientation.Column}
                            contentRef={this.calloutContentRef}
                            contentShadow={true}
                            contentSize={fullscreen ? undefined : size}
                            escDismiss={escDismiss}
                            id={id}
                            focuszoneProps={{
                                circularNavigation: true,
                                defaultActiveElement: this.defaultActiveElement,
                                focusOnMount: true,
                                handleTabKey: true,
                                includeDefaults: true,
                                postprocessKeyStroke: function (event: React.KeyboardEvent<HTMLElement>) {
                                    // We want to prevent moving outside the panel if there are no focusable elements in the panel.
                                    event.which === KeyCode.tab && event.preventDefault();
                                    return FocusZoneKeyStroke.IgnoreParents;
                                }
                            }}
                            lightDismiss={lightDismiss && isDisplayed !== false}
                            modal={modal && isDisplayed !== false}
                            onDismiss={onDismiss}
                            portalProps={portalProps}
                        >
                            <SurfaceContext.Consumer>
                                {surfaceContext => (
                                    <Surface {...surfaceContext} spacing={Spacing.default}>
                                        <div className={css(className, "bolt-panel-root flex-column flex-grow scroll-auto")}>
                                            <div className="bolt-panel-focus-element no-outline" tabIndex={-1} />
                                            {children}
                                        </div>
                                    </Surface>
                                )}
                            </SurfaceContext.Consumer>
                        </Callout>
                    );
                }}
            </Observer>
        );
    }

    // TODO: Uncomment in M150
    /* public componentDidMount() {
        setTimeout(() => {
            this.setState({ isDisplayed: true });
        }, 0);
    }

    public componentWillUnmount() {
        if($DEBUG) {
            if(this.state.isDisplayed === true) {
                console.error("Panel was unmounted while still displayed; call animateOut first and unmount after the returned promise resolves");
            }
        }
    } */

    public animateOut(): Promise<void> {
        return Promise.resolve();

        // TODO: Uncomment in M150
        /* return new Promise(resolve => {
            this.setState({ isDisplayed: false });

            setTimeout(() => {
                resolve();
            }, 200);
        }); */
    }

    private defaultActiveElement = (): string => {
        // We don't ever want the Panel to set focus to the body, so if the defaultActiveElement
        // prop that is provided cannot be found, instead use the panel's default focus element.

        const { defaultActiveElement } = this.props;
        const selector = typeof defaultActiveElement === "function" ? defaultActiveElement() : defaultActiveElement;

        if (selector && this.calloutContentRef.current) {
            const matches = this.calloutContentRef.current.querySelectorAll(selector);
            if (matches && matches.length) {
                return selector;
            }
        }

        return ".bolt-panel-focus-element";
    };
}
